import { useMemo } from 'react'

import { FieldValidator } from './FormValidators'

export type BuiltInNumberValidatorProps = {
  min?: number
  max?: number
  required?: boolean
}

export type BuiltInStringValidatorProps = {
  min?: number
  max?: number
  required?: boolean
  email?: boolean
  url?: boolean
  pattern?: RegExp
}

const builtInValidatorProps = ['min', 'max', 'required', 'email', 'url', 'pattern'] as const

export type BuiltInValidatorProps<V> = V extends number
  ? BuiltInNumberValidatorProps
  : V extends string
    ? BuiltInStringValidatorProps
    : {}

export const useBuiltInValidatorProps = (props: Record<string, any>) => {
  const builtInPropsMemo = () => {
    const builtInProps: Record<string, any> = {}
    for (const prop of builtInValidatorProps) builtInProps[prop] = props[prop] as any
    return builtInProps
  }

  const builtInProps = useMemo(
    builtInPropsMemo,
    builtInValidatorProps.map((p) => props[p]),
  )
  return builtInProps
}

const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
const urlRegex = /^[a-zA-Z0-9+-.]:(.+)?$/

const onCond = <R>(cond: boolean, result: R): R | undefined => {
  if (cond) return result
  return undefined
}

const anyRequired = () => (value: any) => onCond(!value, 'required')

const stringMin = (min: number) => (value: string) => onCond(value.length < min, 'min')
const stringMax = (max: number) => (value: string) => onCond(value.length > max, 'max')
const stringEmail = () => (value: string) => onCond(!emailRegex.test(value), 'email')
const stringUrl = () => (value: string) => onCond(!urlRegex.test(value), 'url')
const stringPattern = (pattern: RegExp) => (value: string) =>
  onCond(!pattern.test(value), 'pattern')

const numberMax = (max: number) => (value: number) => onCond(value > max, 'max')
const numberMin = (min: number) => (value: number) => onCond(value < min, 'min')

const getBuiltInStringValidatorsByProps = (props: BuiltInStringValidatorProps) => {
  const builtInValidators: FieldValidator<string>[] = []
  if (props.min) builtInValidators.push(stringMin(props.min))
  if (props.max) builtInValidators.push(stringMax(props.max))
  if (props.required) builtInValidators.push(anyRequired())
  if (props.email) builtInValidators.push(stringEmail())
  if (props.url) builtInValidators.push(stringUrl())
  if (props.pattern) builtInValidators.push(stringPattern(props.pattern))
  return builtInValidators
}

const getBuiltInNumberValidatorsByProps = (props: BuiltInNumberValidatorProps) => {
  const builtInValidators: FieldValidator<number>[] = []
  if (props.min) builtInValidators.push(numberMin(props.min))
  if (props.max) builtInValidators.push(numberMax(props.max))
  if (props.required) builtInValidators.push(anyRequired())
  return builtInValidators
}

export const getBuiltInValidatorsByProps = <V>(value: V, props: BuiltInValidatorProps<V>) => {
  switch (typeof value) {
    case 'string':
      return getBuiltInStringValidatorsByProps(props) as FieldValidator<V>[]
    case 'number':
      return getBuiltInNumberValidatorsByProps(props) as FieldValidator<V>[]
    default:
      return []
  }
}
